// Setting knowledge for Policy chat: what each agent setting does on an
// endpoint, in the fields the catalog has no place for. The chat model reads
// these entries for the keys the current step writes, so it can explain a
// setting without inventing its behavior.
//
// Codex and Copilot entries take their label from the editor tabs' copy;
// every other field is written here, as for Claude Code.
//
// `override` is what users and repositories can still do when the policy sets
// the key. Claude Code values come from the 2.1.304 per-key source rules
// (policyhelper findings, claude-code-settings-hierarchy.md section 14).
// Copilot values come from its catalog composition. Codex requirements are
// written only by managed sources.
import { CODEX_COPY } from "../codex/help.js";
import { COPILOT_COPY } from "../copilot/help.js";

export const OVERRIDE = Object.freeze({
    fixed: "Users cannot change it.",
    chooseWithin: "Users can pick only from the allowed values.",
    addTightens: "Users can add entries. Their entries can only block or ask more.",
    addWidens: "Users can add entries, and their entries allow more.",
    defaultOnly: "This is a starting value. Users can change it.",
});

const VERIFIED_CLAUDE = "Claude Code 2.1.304 binary read, 2026-10-20.";

const CLAUDE = Object.freeze({
    "/deniedMcpServers": {
        label: "Blocked MCP servers",
        mechanics: "A server that matches an entry by name, command, or URL does not load. Claude Code checks this list before the allowlist and before managed servers, so a blocked server stays blocked even when another list allows it. The change applies at the next session start.",
        override: "addTightens",
        limits: "A claude.ai connector is checked by its name and URL. A server with a different name and a different command or URL is a different server.",
        verified: `${VERIFIED_CLAUDE} Deny entries from every settings source are kept.`,
        practice: "Blocks a server the tenant does not want used. Every other server keeps working, including servers people install later.",
        removal: "Removing an entry lets that server load again at the next session start.",
        evidence: "mcp_servers",
    },
    "/disableClaudeAiConnectors": {
        label: "Block claude.ai connectors",
        mechanics: "No connector that a person added in claude.ai loads in Claude Code, approved or not.",
        override: "fixed",
        limits: "Connectors are not in the MCP server list, so a server block cannot reach them.",
        verified: `${VERIFIED_CLAUDE} True from any settings source turns it on.`,
        practice: "Closes the connector path when the tenant blocks servers. People lose every connector, including approved ones.",
        removal: "Connectors load again at the next session start.",
        evidence: null,
    },
    "/permissions/allow": {
        label: "Run without asking",
        mechanics: "A tool call that matches a rule runs without a prompt, unless a deny or ask rule also matches. Bash rules match a command; `Bash(git push:*)` matches every command that starts with git push.",
        override: "addWidens",
        limits: "Users and projects can add their own allow rules unless the policy also sets allowManagedPermissionRulesOnly.",
        verified: "Claude Code 2.1.296 binary read, 2026-10-13. Rules from every source are kept; deny beats ask, ask beats allow.",
        practice: "Removes prompts for routine commands. Each rule lets an agent run that command with no person watching.",
        removal: "Matching commands ask again, unless the person's own rules allow them.",
        evidence: null,
    },
    "/permissions/ask": {
        label: "Request approval",
        mechanics: "A matching tool call requires approval even when an allow rule matches. A deny rule still takes precedence.",
        override: "addTightens",
        limits: "In auto mode the automatic reviewer can answer the prompt.",
        verified: "Claude Code 2.1.296 binary read, 2026-10-13.",
        practice: "Adds an approval check for commands that change shared systems. Who answers depends on the active permission mode.",
        removal: "Matching commands follow the person's own rules again.",
        evidence: null,
    },
    "/permissions/deny": {
        label: "Block",
        mechanics: "A tool call that matches a rule is refused. The agent gets an error and continues without it.",
        override: "addTightens",
        limits: "Bash rules match the command text. A command reached another way, for example inside a script, is not matched.",
        verified: "Claude Code 2.1.296 binary read, 2026-10-13. A deny rule from any source wins.",
        practice: "Stops commands the tenant never wants an agent to run.",
        removal: "Matching commands follow the other rules again.",
        evidence: null,
    },
    "/permissions/disableBypassPermissionsMode": {
        label: "Turn off bypass mode",
        mechanics: "Claude Code refuses --dangerously-skip-permissions and the bypass permission mode.",
        override: "fixed",
        limits: null,
        verified: VERIFIED_CLAUDE,
        practice: "Every session keeps its approval prompts and rules. People who run long autonomous sessions get prompts again.",
        removal: "People can start bypass sessions again.",
        evidence: null,
    },
    "/allowManagedPermissionRulesOnly": {
        label: "Use only the policy's permission rules",
        mechanics: "Permission rules from user, project, local and --settings files are ignored, and --allowedTools allow rules are dropped.",
        override: "fixed",
        limits: "Users also lose their own deny rules.",
        verified: "Claude Code 2.1.296 binary read, 2026-10-13.",
        practice: "Makes the policy the only source of allow rules. People can no longer pre-approve their own commands.",
        removal: "User and project rules apply again.",
        evidence: null,
    },
    "/disableRemoteControl": {
        label: "Turn off remote control",
        mechanics: "claude remote-control and --remote are refused, so no session can be driven from another device.",
        override: "fixed",
        limits: null,
        verified: VERIFIED_CLAUDE,
        practice: "Keeps every session under the control of the person at the machine.",
        removal: "Remote control works again.",
        evidence: null,
    },
    "/browserExternalPageTools": {
        label: "Stop agent actions on external web pages",
        mechanics: "Claude's tools cannot act on external pages in the Claude Desktop browser pane.",
        override: "fixed",
        limits: "Claude Desktop only. The CLI has no browser pane.",
        verified: "Not verified: the CLI binary does not read this key.",
        practice: "Stops an agent from submitting forms or clicking on sites outside the tenant.",
        removal: "Agents can act on external pages again.",
        evidence: null,
    },
    "/disableWorkflows": {
        label: "Turn off workflows",
        mechanics: "Dynamic workflows, which start many subagents for one task, are not offered.",
        override: "fixed",
        limits: null,
        verified: VERIFIED_CLAUDE,
        practice: "Limits how much work one request can start. Large tasks run in one session instead.",
        removal: "Workflows are offered again.",
        evidence: null,
    },
    "/disableArtifact": {
        label: "Turn off publishing to a web page",
        mechanics: "The Artifact tool, which publishes output to a hosted web page, is not available.",
        override: "fixed",
        limits: null,
        verified: VERIFIED_CLAUDE,
        practice: "Stops agents from publishing session output outside the machine.",
        removal: "The Artifact tool is available again.",
        evidence: null,
    },
    "/disableBundledSkills": {
        label: "Turn off bundled skills",
        mechanics: "Skills that ship with Claude Code are not loaded.",
        override: "fixed",
        limits: null,
        verified: VERIFIED_CLAUDE,
        practice: "Keeps only skills the tenant or the person installed.",
        removal: "Bundled skills load again.",
        evidence: null,
    },
    "/disableSkillShellExecution": {
        label: "Turn off shell commands in skills",
        mechanics: "Inline shell commands inside skills do not run.",
        override: "fixed",
        limits: null,
        verified: `${VERIFIED_CLAUDE} The managed value is read first.`,
        practice: "Stops a skill from running commands when it loads. Some skills stop working.",
        removal: "Skill shell commands run again.",
        evidence: null,
    },
});

// Codex and Copilot: what each setting does (what, how, who, why, skip and
// notCovered), for the settings Policy chat writes. The tabs show shorter
// text; the chat model needs the mechanics and limits.
const CODEX_HELP = Object.freeze({
    "/rules/prefix_rules": {
        what: "Commands that must ask first, and commands that can never run.",
        how: "A rule matches a command by its first words, in order. The rule git push matches git push origin main. It does not match git status. Prompt requires approval from the configured reviewer. Forbidden refuses the command, so the person cannot approve it.",
        who: "Every person using Codex on a managed machine.",
        why: "A matching command requires approval or is refused. Approval settings and other matching rules still apply.",
        notCovered: "A command with the same effect under a different name. A rule on rm does not cover a script that deletes files.",
        skip: "These rules can request approval or refuse commands. They cannot allow commands. Read allowed_approvals_reviewers and allowed_approval_policies before promising a human prompt; the reviewer may be automatic and never mode cannot request approval.",
    },
    "/allowed_approvals_reviewers": {
        what: "Which approval reviewers people may select.",
        how: "user means the person answers; auto_review means an automatic reviewer answers. If both are allowed, the active configuration chooses the reviewer.",
        skip: "Allowing the user reviewer does not force it when auto_review is also allowed. This setting does not itself create command rules.",
    },
    "/allowed_approval_policies": {
        what: "Which approval policies people may select in Codex.",
        how: "On request sends commands that need approval to the configured reviewer. Never does not request approval, so commands that require it cannot run. Granular requests approval for configured action types. Untrusted is selected by Codex for a project the person marked untrusted, rather than being a selectable option.",
        who: "Every person using Codex on a managed machine.",
        why: "Excluding Never prevents selection of no-request mode; it does not require approval for every command or select the human reviewer.",
        skip: "Command prefix rules and other execution checks decide which commands need approval or are refused. allowed_approvals_reviewers constrains who may answer requests.",
    },
    "/allow_remote_control": {
        what: "Whether the Codex app can take instructions from another device.",
        how: "Remote Control lets another device send instructions to the Codex app on this machine.",
        who: "People who use the Codex app on a managed machine.",
        why: "Off turns Remote Control off. On removes this restriction only. It does not make the feature available.",
    },
    "/allowed_web_search_modes": {
        what: "Whether Codex can search the web, and how.",
        how: "Disabled means no web search. Cached means results come from an index OpenAI keeps, with no live request to other sites. Indexed means live requests go only to pages that index already knows. Live means any web request. Each checked mode is one people may pick. Disabled is always available.",
        who: "Every person using Codex on a managed machine.",
        why: "An unchecked mode cannot be used. With only Disabled checked, Codex cannot search the web.",
        skip: "This setting decides web search only. It does not control network access for the commands Codex runs.",
    },
    "/features": {
        what: "Codex features turned on or off for everyone.",
        how: "Each entry turns one feature on or off. Whether a feature exists also depends on the Codex version, the app, the model, and the account.",
        who: "Every person using Codex on a managed machine.",
        why: "A listed feature is on or off for everyone. If a person's own settings disagree, Codex shows a configuration error and does not start.",
        skip: "This setting decides listed features only. Other features follow the person's own settings.",
    },
});

const COPILOT_HELP = Object.freeze({
    "/deniedMcpServers": {
        what: "MCP servers that never load.",
        how: "Each entry matches a server by URL, by exact command, or by name. A blocked server stays blocked even if the allowlist matches it.",
        who: "Every person using Copilot on a managed machine.",
        notCovered: "Built-in GitHub servers cannot be blocked.",
    },
    "/permissions/allow": {
        what: "Operations Copilot runs without a prompt.",
        how: "Each entry is a permission rule. When more than one managed source has an allow list, every list must admit the operation.",
        who: "Every person using Copilot on a managed machine.",
        skip: "Block and approval rules still win over these rules.",
    },
    "/permissions/ask": {
        what: "Operations that need the person's approval each time.",
        how: "Each entry is a permission rule. Ask wins over allow rules. Rules from every managed source are kept.",
        who: "Every person using Copilot on a managed machine.",
        why: "A matching operation always shows a prompt, even when a person's own rules allow it.",
    },
    "/permissions/deny": {
        what: "Operations that match a rule are blocked.",
        how: "Each entry is a permission rule, for example shell(rm) or Domain(example.com). A block wins over ask and allow rules. Rules from every managed source are kept.",
        who: "Every person using Copilot on a managed machine.",
        why: "People cannot approve a matching operation.",
    },
    "/permissions/disableBypassPermissionsMode": {
        what: "The allow-all mode, also called yolo mode, and the allow-all flags for tools, paths and URLs.",
        how: "Disable blocks the combined mode and each separate allow-all flag. Normal approval prompts still work.",
        who: "Every person using Copilot on a managed machine.",
        why: "People cannot approve every operation in advance.",
    },
    "/remoteControl": {
        what: "Whether a Copilot CLI session on this machine can be controlled from another device.",
        how: "Enabled allows it. Disabled blocks it. Require SSO allows it only for an account that is SSO-authorized in a listed GitHub.com organization or signed in to a listed GitHub Enterprise Cloud domain.",
        who: "People who use Copilot CLI on a managed machine.",
        why: "Disabled keeps every session under the control of the person at the machine.",
    },
});

// The fields the tables above lack.
const CODEX_EXTRA = Object.freeze({
    "/rules/prefix_rules": { override: "fixed", evidence: null, removal: "Matching commands follow the person's approval choice again." },
    "/allowed_approvals_reviewers": { override: "chooseWithin", evidence: null, removal: "People can pick any supported approval reviewer again." },
    "/allowed_approval_policies": { override: "chooseWithin", evidence: null, removal: "People can pick any approval policy again." },
    "/allow_remote_control": { override: "fixed", evidence: null, removal: "Remote control works again." },
    "/allowed_web_search_modes": { override: "chooseWithin", evidence: null, removal: "People can pick any web search mode again." },
    "/features": { override: "fixed", evidence: null, removal: "Each feature returns to its Codex default." },
});

const COPILOT_EXTRA = Object.freeze({
    "/deniedMcpServers": { override: "addTightens", evidence: "mcp_servers", removal: "Removing an entry lets that server load again." },
    "/permissions/allow": { override: "chooseWithin", evidence: null, removal: "Matching commands ask again." },
    "/permissions/ask": { override: "addTightens", evidence: null, removal: "Matching commands follow the other rules again." },
    "/permissions/deny": { override: "addTightens", evidence: null, removal: "Matching commands follow the other rules again." },
    "/permissions/disableBypassPermissionsMode": { override: "fixed", evidence: null, removal: "People can use allow-all mode again." },
    "/remoteControl": { override: "fixed", evidence: null, removal: "Remote control follows the person's settings again." },
});

function fromHelp(copy, help, extra, pointer, verified) {
    const lookup = pointer.startsWith("/features/") ? "/features" : pointer;
    const h = help[lookup];
    const x = extra[lookup];
    if (!h || !x) return null;
    const [label] = copy[lookup] ?? [lookup];
    return {
        label,
        mechanics: [h.what, h.how, h.who].filter(Boolean).join(" "),
        override: x.override,
        limits: h.notCovered ?? h.skip ?? null,
        verified,
        practice: h.why ?? null,
        removal: x.removal,
        evidence: x.evidence,
    };
}

/// The knowledge entry for one setting, or null when none is written.
export function settingKnowledge(agentId, pointer) {
    if ((agentId === "claude-code" && pointer === "/autoMode/environment") || (agentId === "codex" && pointer === "/guardian_extra_policy")) return {
        label: "Automatic reviewer guidance", mechanics: "Supplies contextual guidance when the automatic reviewer runs; does not enable the reviewer.",
        override: "fixed", limits: "Prompt guidance is not a deterministic prohibition.", verified: "Origin managed configuration catalog.",
        practice: "Explain the risk and legitimate exceptions; test both in a pilot.",
        removal: "Removes this managed guidance; other reviewer settings remain.", evidence: null,
        interactions: agentId === "codex" ? "Managed extra policy replaces the user's own extra reviewer text." : "Used by auto mode; existing environment entries are retained.",
    };
    if (agentId === "claude-code") return CLAUDE[pointer] ?? null;
    if (agentId === "codex") return fromHelp(CODEX_COPY, CODEX_HELP, CODEX_EXTRA, pointer, "Codex source and requirements reference.");
    if (agentId === "github-copilot") return fromHelp(COPILOT_COPY, COPILOT_HELP, COPILOT_EXTRA, pointer, "GitHub managed-settings reference and the CLI 1.0.93 schema.");
    return null;
}

/// One line of plain text per field, for the model input.
export function knowledgeText(agentLabel, pointer, entry) {
    const lines = [`${agentLabel} ${pointer.slice(1).split("/").join(".")} (${entry.label}): ${entry.mechanics}`];
    lines.push(`  Users: ${OVERRIDE[entry.override]}`);
    if (entry.limits) lines.push(`  Limits: ${entry.limits}`);
    if (entry.practice) lines.push(`  Tradeoff: ${entry.practice}`);
    if (entry.removal) lines.push(`  Removal: ${entry.removal}`);
    lines.push(`  Verified: ${entry.verified}`);
    return lines.join("\n");
}
